import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Plus, X } from 'lucide-react';
import { Combobox, type ComboboxOption } from './ui/combobox';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { Button } from './ui/button';
import { useDimensionsList } from '../hooks/useDimension';
import { useMeasures } from '../hooks/useMeasures';
import { useUniqueValuesInDimension } from '../services/reportBuilder.service';

/**
 * Metric-blacklist picker for DemoConfig.blacklisted_metrics — same idea as
 * the dimension filters above: real measure names via MultiSelectCombobox
 * instead of hand-typed ids.
 */
export function DemoConfigBlacklistedMetrics({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const { data: measuresData, isLoading } = useMeasures({ pageSize: 500 });
  const options = useMemo(
    () => (measuresData?.records || []).map((m) => ({ value: m.id, label: m.name })),
    [measuresData]
  );

  return (
    <MultiSelectCombobox
      options={options}
      value={value}
      onValueChange={onChange}
      placeholder="Select metrics to hide..."
      emptyText="No metrics available."
      isLoading={isLoading}
    />
  );
}

interface Row {
  key: string;
  dimensionId: string;
  values: string[];
}

let rowKeySeq = 0;
function nextRowKey() {
  rowKeySeq += 1;
  return `row-${rowKeySeq}`;
}

/**
 * Dimension → allowed-values picker for DemoConfig.dimension_filters
 * (Dict[dimension_id, string[]], always IN semantics — no comparator, unlike
 * the report-builder's AnalysisDimensionFilter). Reuses the same Combobox /
 * MultiSelectCombobox primitives and useDimensionsList / real-value-fetching
 * hook as the user-permissions "Dimension Filters" editor, so an admin picks
 * real dimensions and real values instead of typing dimension ids/values
 * by hand.
 */
export function DemoConfigDimensionFilters({
  value,
  onChange,
}: {
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    Object.entries(value).map(([dimensionId, values]) => ({
      key: nextRowKey(),
      dimensionId,
      values,
    }))
  );

  const { data: dimensionsData } = useDimensionsList({ pageSize: 500 });
  const dimensionOptions: ComboboxOption[] = useMemo(
    () => (dimensionsData?.records || []).map((d) => ({ value: d.id, label: d.name })),
    [dimensionsData]
  );

  const emit = (nextRows: Row[]) => {
    setRows(nextRows);
    const next: Record<string, string[]> = {};
    for (const row of nextRows) {
      if (row.dimensionId) next[row.dimensionId] = row.values;
    }
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <DemoConfigDimensionFilterRow
          key={row.key}
          row={row}
          dimensionOptions={dimensionOptions.filter(
            (opt) =>
              opt.value === row.dimensionId ||
              !rows.some((r) => r.dimensionId === opt.value)
          )}
          onChange={(next) => emit(rows.map((r) => (r.key === row.key ? next : r)))}
          onDelete={() => emit(rows.filter((r) => r.key !== row.key))}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => emit([...rows, { key: nextRowKey(), dimensionId: '', values: [] }])}
      >
        <Plus className="h-4 w-4 mr-1" />
        Add dimension filter
      </Button>
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No dimension filters — the demo will see unrestricted data for this tenant. Add at
          least one to scope what the prospect can see.
        </p>
      )}
    </div>
  );
}

function DemoConfigDimensionFilterRow({
  row,
  dimensionOptions,
  onChange,
  onDelete,
}: {
  row: Row;
  dimensionOptions: ComboboxOption[];
  onChange: (row: Row) => void;
  onDelete: () => void;
}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);
  const { data: dimensionValues, isLoading } = useUniqueValuesInDimension(
    row.dimensionId || null,
    debouncedQuery
  );

  const valueOptions = useMemo(
    () =>
      (dimensionValues || []).map((v) => ({
        value: v,
        label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
      })),
    [dimensionValues]
  );

  return (
    <div className="flex items-center gap-2">
      <Combobox
        options={dimensionOptions}
        value={row.dimensionId}
        onValueChange={(dimensionId) => onChange({ ...row, dimensionId, values: [] })}
        placeholder="Select dimension..."
        emptyText="No dimensions available."
        className="w-[220px] shrink-0"
      />
      <MultiSelectCombobox
        options={valueOptions}
        value={row.values}
        onValueChange={(values) => onChange({ ...row, values })}
        placeholder="Select allowed values..."
        emptyText="No values available."
        onInputChange={setQuery}
        isLoading={isLoading}
        disabled={!row.dimensionId}
        className="flex-1"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onDelete}
        className="h-8 w-8 p-0 shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
